import React, { useMemo, useState } from 'react';
import { Box, Flex, IconButton, Spinner, Text } from '@chakra-ui/react';
import { CaretLeft, CaretRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard } from '../../../../../components/glass';
import { IEmpresaSaude, useContaCilEmpresasSaude } from '../../../../../hooks/useContaCilEmpresas';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const PAGE = 8;
type Filtro = 'todas' | 'pendencias' | 'sempoc';

function status(e: IEmpresaSaude): { label: string; cor: string } {
  if (e.pendentes > 0) return { label: 'Pendente', cor: '#E08A06' };
  if (e.lancamentos_total > 0) return { label: 'Conciliada', cor: '#0EA371' };
  return { label: 'Sem movimento', cor: '#94A3B8' };
}

const Chip = ({
  ativo,
  onClick,
  children,
  n,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
  n: number;
}) => (
  <Box
    as="button"
    onClick={onClick}
    px={3}
    py={1.5}
    borderRadius="full"
    fontSize="12px"
    fontWeight="600"
    bg={ativo ? '#7C3AED' : 'rgba(15,23,42,0.04)'}
    color={ativo ? 'white' : '#64748B'}
  >
    {children} <span style={{ opacity: 0.7 }}>{n}</span>
  </Box>
);

export const EmpresasTabela = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useContaCilEmpresasSaude();
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [page, setPage] = useState(1);

  const itens = data?.itens ?? [];
  const contagem = useMemo(
    () => ({
      todas: itens.length,
      pendencias: itens.filter(e => e.pendentes > 0).length,
      sempoc: itens.filter(e => e.qtd_sem_poc > 0).length,
    }),
    [itens],
  );
  const filtrados = useMemo(() => {
    if (filtro === 'pendencias') return itens.filter(e => e.pendentes > 0);
    if (filtro === 'sempoc') return itens.filter(e => e.qtd_sem_poc > 0);
    return itens;
  }, [itens, filtro]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / PAGE));
  const pageSafe = Math.min(page, totalPages);
  const visiveis = filtrados.slice((pageSafe - 1) * PAGE, pageSafe * PAGE);

  const trocarFiltro = (f: Filtro) => {
    setFiltro(f);
    setPage(1);
  };

  return (
    <GlassCard intensity="medium" p={6}>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            Acompanhamento
          </Text>
          <Text fontSize="15px" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            Por empresa
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap">
          <Chip ativo={filtro === 'todas'} onClick={() => trocarFiltro('todas')} n={contagem.todas}>
            Todas
          </Chip>
          <Chip
            ativo={filtro === 'pendencias'}
            onClick={() => trocarFiltro('pendencias')}
            n={contagem.pendencias}
          >
            Com pendências
          </Chip>
          <Chip
            ativo={filtro === 'sempoc'}
            onClick={() => trocarFiltro('sempoc')}
            n={contagem.sempoc}
          >
            Contas sem POC
          </Chip>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner color="#7C3AED" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Box minW="640px">
            {/* header */}
            <Box
              display="grid"
              gridTemplateColumns="2.4fr 0.7fr 1fr 1fr 1fr 1.6fr"
              gap={3}
              px={3}
              pb={2}
              borderBottomWidth="1px"
              borderColor="rgba(15,23,42,0.08)"
            >
              {['Empresa', 'Contas', 'Lançam.', 'Concil.', 'Pend.', 'Conciliação'].map((h, i) => (
                <Text
                  key={h}
                  fontSize="10px"
                  fontWeight="700"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  textAlign={i === 0 ? 'left' : 'right'}
                >
                  {h}
                </Text>
              ))}
            </Box>
            {visiveis.map(e => {
              const st = status(e);
              const pct = e.percent_conciliado;
              return (
                <Box
                  as="button"
                  key={e.empresa_id}
                  display="grid"
                  gridTemplateColumns="2.4fr 0.7fr 1fr 1fr 1fr 1.6fr"
                  gap={3}
                  px={3}
                  py={2.5}
                  alignItems="center"
                  w="full"
                  textAlign="left"
                  borderBottomWidth="1px"
                  borderColor="rgba(15,23,42,0.04)"
                  _hover={{ bg: 'rgba(124,58,237,0.04)' }}
                  onClick={() =>
                    navigate(
                      `/modulesHome/accountingConciliation/conciliacao-contabil/empresas?empresa=${e.empresa_id}`,
                    )
                  }
                >
                  <Box minW={0}>
                    <Text fontSize="12.5px" fontWeight="600" color="#0F172A" noOfLines={1}>
                      {e.razao_social || e.nome || `Empresa ${e.empresa_id}`}
                    </Text>
                    <Text fontSize="10px" color={st.cor} fontWeight="600">
                      {st.label}
                    </Text>
                  </Box>
                  <Text
                    fontSize="12px"
                    color="#64748B"
                    textAlign="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {e.qtd_contas}
                  </Text>
                  <Text
                    fontSize="12px"
                    color="#0F172A"
                    textAlign="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(e.lancamentos_total)}
                  </Text>
                  <Text
                    fontSize="12px"
                    color="#0EA371"
                    textAlign="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(e.conciliados)}
                  </Text>
                  <Text
                    fontSize="12px"
                    color={e.pendentes > 0 ? '#E08A06' : '#94A3B8'}
                    textAlign="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(e.pendentes)}
                  </Text>
                  <Flex align="center" gap={2}>
                    <Box
                      flex={1}
                      h="6px"
                      borderRadius="full"
                      bg="rgba(15,23,42,0.06)"
                      overflow="hidden"
                    >
                      <Box h="full" w={`${pct}%`} bg={pct >= 100 ? '#0EA371' : '#7C3AED'} />
                    </Box>
                    <Text
                      fontSize="11px"
                      fontWeight="700"
                      color="#0F172A"
                      w="34px"
                      textAlign="right"
                    >
                      {pct.toFixed(0)}%
                    </Text>
                  </Flex>
                </Box>
              );
            })}
            {visiveis.length === 0 && (
              <Text fontSize="13px" color="#94A3B8" textAlign="center" py={8}>
                Nenhuma empresa neste filtro.
              </Text>
            )}
          </Box>
        </Box>
      )}

      {totalPages > 1 && (
        <Flex align="center" justify="center" gap={4} mt={4}>
          <IconButton
            aria-label="Anterior"
            icon={<CaretLeft size={14} weight="bold" />}
            size="xs"
            variant="ghost"
            isDisabled={pageSafe <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          />
          <Text fontSize="11px" color="#64748B" fontWeight="600">
            {pageSafe} de {totalPages}
          </Text>
          <IconButton
            aria-label="Próxima"
            icon={<CaretRight size={14} weight="bold" />}
            size="xs"
            variant="ghost"
            isDisabled={pageSafe >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          />
        </Flex>
      )}
    </GlassCard>
  );
};
